<template>
  <div class="app-root">
    <!-- ═══════════════════════════════════════════════
         SÉLECTEUR DE VUE (demo routing)
    ════════════════════════════════════════════════ -->
    <div class="view-switcher">
      <button
        v-for="v in views"
        :key="v.key"
        @click="currentView = v.key"
        :class="['view-tab', { active: currentView === v.key }]"
      >
        {{ v.label }}
      </button>
    </div>

    <!-- ═══════════════════════════════════════════════
         VUE 1 — DASHBOARD SUPERVISEUR
    ════════════════════════════════════════════════ -->
    <div v-if="currentView === 'supervisor'" class="supervisor-dashboard">
      <dashboard-context-header title="ANIMATION REPAS" @context-change="onContextChange">
        <template #extra>
          <div class="vote-summary-bar" v-if="childVotes.length > 0">
            <span class="vote-summary-label">🗳️ Votes enfants : {{ childVotes.length }}</span>
            <span class="vote-pill pill-green">😄 {{ childVoteSummary.liked }}</span>
            <span class="vote-pill pill-orange">😐 {{ childVoteSummary.medium }}</span>
            <span class="vote-pill pill-red">😞 {{ childVoteSummary.disliked }}</span>
            <span class="vote-pill pill-blue">🍽️ Assez : {{ childVoteSummary.enoughYes }}</span>
            <span class="vote-pill pill-red">🍽️ Pas assez : {{ childVoteSummary.enoughNo }}</span>
          </div>
        </template>
      </dashboard-context-header>

      <!-- Cartes principales -->
      <div class="cards-grid">
        <!-- Card 1 -->
        <div class="card card-featured" @click="showGlobalFeedback = true">
          <div class="card-header">
            <h3 class="card-title">😊 Appréciation globale</h3>
            <span class="badge badge-green">{{ getRatingLabel(globalRating) }}</span>
          </div>
          <p class="card-description">Donner votre évaluation du repas</p>
          <button class="card-link">Évaluer →</button>
        </div>

        <!-- Card 2 -->
        <div class="card" @click="showDetailedFeedback = true">
          <div class="card-header">
            <h3 class="card-title">🍽️ Détail par plat</h3>
            <span class="badge badge-blue">{{ dishes.length }} plats</span>
          </div>
          <p class="card-description">Noter chaque plat individuellement</p>
          <button class="card-link">Noter →</button>
        </div>

        <!-- Card 3 -->
        <div class="card" @click="showContextForm = true">
          <div class="card-header">
            <h3 class="card-title">🌤️ Contexte du jour</h3>
          </div>
          <p class="card-description">Météo, ambiance et observations</p>
          <button class="card-link">Renseigner →</button>
        </div>

        <!-- Card 4 -->
        <div v-if="featureCommunications" class="card" @click="showCommunications = true">
          <div class="card-header">
            <h3 class="card-title">📢 Infos du jour</h3>
          </div>
          <p class="card-description">Messages et alertes importantes</p>
          <button class="card-link">Voir →</button>
        </div>

        <!-- Card 5 -->
        <div v-if="featureWaste" class="card card-highlight" @click="showWasteToKids = true">
          <div class="card-header">
            <h3 class="card-title">👀 Montrer aux enfants</h3>
          </div>
          <p class="card-description">Visualisation ludique du gaspillage</p>
          <button class="card-link">Afficher →</button>
        </div>

        <!-- Card 6 -->
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">💬 Ajouter</h3>
          </div>
          <div class="action-buttons">
            <button @click="addComment" class="action-btn">
              <span class="action-icon">💬</span>
              <span>Commentaire</span>
            </button>
            <button @click="takePhoto" class="action-btn">
              <span class="action-icon">📸</span>
              <span>Photo</span>
            </button>
          </div>
        </div>

        <!-- Card 7 — NOUVELLE : Résultats votes enfants (MVP 5 & 6) -->
        <div v-if="featureVotes" class="card card-votes" @click="showVoteResults = true">
          <div class="card-header">
            <h3 class="card-title">🗳️ Votes enfants</h3>
            <span class="badge badge-purple">{{ childVotes.length }} votes</span>
          </div>
          <p class="card-description">Résultats du vote numérique en temps réel</p>
          <div class="vote-mini-bars" v-if="childVotes.length > 0">
            <div class="mini-bar-row">
              <span>😄</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-green" :style="{ width: pct(childVoteSummary.liked) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.liked) }}%</span>
            </div>
            <div class="mini-bar-row">
              <span>😐</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-orange" :style="{ width: pct(childVoteSummary.medium) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.medium) }}%</span>
            </div>
            <div class="mini-bar-row">
              <span>😞</span>
              <div class="mini-bar-track">
                <div class="mini-bar-fill fill-red" :style="{ width: pct(childVoteSummary.disliked) + '%' }"></div>
              </div>
              <span class="mini-bar-val">{{ pct(childVoteSummary.disliked) }}%</span>
            </div>
          </div>
          <p v-else class="card-description" style="font-style: italic">Aucun vote pour le moment</p>
          <button class="card-link">Consulter →</button>
        </div>

        <!-- Card 8 — NOUVELLE : Lancer la borne vote enfant -->
        <div v-if="featureVotes" class="card card-kiosk" @click="currentView = 'kiosk'">
          <div class="card-header">
            <h3 class="card-title">📲 Borne de vote</h3>
          </div>
          <p class="card-description">Ouvrir l'écran de vote pour les enfants (tablette dédiée)</p>
          <button class="card-link">Lancer la borne →</button>
        </div>
      </div>

      <!-- ─── MODALS SUPERVISEUR ─────────────────────────── -->

      <!-- Modal: Appréciation globale -->
      <div v-if="showGlobalFeedback" class="modal-overlay" @click="showGlobalFeedback = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showGlobalFeedback = false">✕</button>
          <h2 class="modal-title">😊 Appréciation globale</h2>
          <p class="modal-subtitle">Comment s'est passé le repas ?</p>
          <div class="rating-selector">
            <button
              v-for="(emoji, idx) in emojis"
              :key="idx"
              @click="globalRating = idx + 1"
              :class="{ active: globalRating === idx + 1 }"
              class="rating-btn"
            >
              <span class="rating-emoji">{{ emoji }}</span>
              <span class="rating-label">{{ getRatingLabelByIndex(idx) }}</span>
            </button>
          </div>
          <button v-if="globalRating" @click="saveGlobalRating" class="btn btn-primary">Enregistrer</button>
        </div>
      </div>

      <!-- Modal: Détail par plat -->
      <div v-if="showDetailedFeedback" class="modal-overlay" @click="showDetailedFeedback = false">
        <div class="modal-content modal-large" @click.stop>
          <button class="modal-close" @click="showDetailedFeedback = false">✕</button>
          <h2 class="modal-title">🍽️ DÉTAIL PAR PLAT</h2>
          <div class="dishes-list">
            <div v-for="(dish, idx) in dishes" :key="idx" class="dish-card">
              <div class="flex justify-between items-start mb-3">
                <div>
                  <h4 class="dish-name">{{ dish.name }}</h4>
                  <p class="text-xs text-gray-500">{{ dish.category }}</p>
                </div>
              </div>
              <div class="mb-3">
                <p class="text-xs text-gray-600 mb-1">Appréciation</p>
                <div class="dish-ratings">
                  <button
                    v-for="(emoji, i) in emojis"
                    :key="i"
                    @click="updateDishRating(idx, i + 1)"
                    :class="['rating-mini-btn', { active: dish.rating === i + 1 }]"
                  >
                    {{ emoji }}
                  </button>
                  <span class="rating-percent">{{ dish.rating ? getRatingLabel(dish.rating) : "" }}</span>
                </div>
              </div>
              <div v-if="dish.comment" class="dish-comment">💬 "{{ dish.comment }}"</div>
              <button @click="editDishComment(idx)" class="text-xs text-blue-600 mt-2 hover:underline">
                {{ dish.comment ? "Modifier" : "Ajouter" }} un commentaire
              </button>
            </div>
          </div>
          <button @click="showDetailedFeedback = false" class="btn btn-primary">Terminé</button>
        </div>
      </div>

      <!-- Modal: Contexte -->
      <div v-if="showContextForm" class="modal-overlay" @click="showContextForm = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showContextForm = false">✕</button>
          <h2 class="modal-title">🌤️ Contexte du jour</h2>
          <div class="detail-section">
            <h3 class="section-title">Météo</h3>
            <div class="weather-selector">
              <button
                v-for="w in weatherOptions"
                :key="w.key"
                @click="weather = w.key"
                :class="{ active: weather === w.key }"
                class="weather-btn"
              >
                <span class="weather-icon">{{ w.icon }}</span>
                <span class="weather-label">{{ w.label }}</span>
              </button>
            </div>
          </div>
          <div class="detail-section">
            <h3 class="section-title">Température</h3>
            <div class="temperature-input">
              <input v-model.number="temperature" type="number" class="input-field" placeholder="22" />
              <span class="temperature-unit">°C</span>
            </div>
          </div>
          <div class="detail-section">
            <h3 class="section-title">Niveau sonore</h3>
            <div class="noise-selector">
              <button
                v-for="n in noiseOptions"
                :key="n.key"
                @click="noiseLevel = n.key"
                :class="{ active: noiseLevel === n.key }"
                class="noise-btn"
              >
                <span class="noise-icon">{{ n.icon }}</span>
                <span class="noise-label">{{ n.label }}</span>
              </button>
            </div>
          </div>
          <button @click="saveContext" class="btn btn-primary">Enregistrer</button>
        </div>
      </div>

      <!-- Modal: Communications -->
      <div v-if="showCommunications && featureCommunications" class="modal-overlay" @click="showCommunications = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showCommunications = false">✕</button>
          <communication-panel2
            :canteen-id="canteenId"
            :date="selectedDate"
            :service-id="selectedService"
            :variant="effectifVariant"
            :readonly="true"
          />
        </div>
      </div>

      <!-- Modal: Résultats votes enfants (MVP 5 & 6) -->
      <div v-if="showVoteResults && featureVotes" class="modal-overlay" @click="showVoteResults = false">
        <div class="modal-content modal-large" @click.stop>
          <button class="modal-close" @click="showVoteResults = false">✕</button>
          <h2 class="modal-title">🗳️ Résultats — Votes enfants</h2>
          <p class="modal-subtitle">{{ childVotes.length }} vote(s) enregistré(s) ce service</p>

          <!-- Section Q1 : Repas aimé -->
          <div class="result-section">
            <h3 class="section-title">😋 As-tu aimé le repas ?</h3>
            <div class="result-bars">
              <div class="result-row">
                <span class="result-label">😄 Oui</span>
                <div class="result-track">
                  <div class="result-fill fill-green" :style="{ width: pct(childVoteSummary.liked) + '%' }"></div>
                </div>
                <span class="result-count">{{ childVoteSummary.liked }} ({{ pct(childVoteSummary.liked) }}%)</span>
              </div>
              <div class="result-row">
                <span class="result-label">😐 Moyen</span>
                <div class="result-track">
                  <div class="result-fill fill-orange" :style="{ width: pct(childVoteSummary.medium) + '%' }"></div>
                </div>
                <span class="result-count">{{ childVoteSummary.medium }} ({{ pct(childVoteSummary.medium) }}%)</span>
              </div>
              <div class="result-row">
                <span class="result-label">😞 Non</span>
                <div class="result-track">
                  <div class="result-fill fill-red" :style="{ width: pct(childVoteSummary.disliked) + '%' }"></div>
                </div>
                <span class="result-count">
                  {{ childVoteSummary.disliked }} ({{ pct(childVoteSummary.disliked) }}%)
                </span>
              </div>
            </div>
          </div>

          <!-- Section Q2 : Assez mangé -->
          <div class="result-section">
            <h3 class="section-title">🍽️ As-tu assez mangé ?</h3>
            <div class="result-bars">
              <div class="result-row">
                <span class="result-label">✅ Oui</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-green"
                    :style="{
                      width:
                        pctOf(childVoteSummary.enoughYes, childVoteSummary.enoughYes + childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.enoughYes }}</span>
              </div>
              <div class="result-row">
                <span class="result-label">❌ Non</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-red"
                    :style="{
                      width:
                        pctOf(childVoteSummary.enoughNo, childVoteSummary.enoughYes + childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.enoughNo }}</span>
              </div>
            </div>
          </div>

          <!-- Section Q3 conditionnelle : Pourquoi pas assez ? (MVP 6) -->
          <div class="result-section" v-if="childVoteSummary.enoughNo > 0">
            <h3 class="section-title">
              🔍 Pourquoi pas assez ?
              <span class="badge badge-orange">{{ childVoteSummary.enoughNo }} réponses</span>
            </h3>
            <div class="result-bars">
              <div class="result-row" v-for="reason in notEnoughReasons" :key="reason.key">
                <span class="result-label">{{ reason.icon }} {{ reason.label }}</span>
                <div class="result-track">
                  <div
                    class="result-fill fill-orange"
                    :style="{
                      width: pctOf(childVoteSummary.reasons[reason.key] || 0, childVoteSummary.enoughNo) + '%',
                    }"
                  ></div>
                </div>
                <span class="result-count">{{ childVoteSummary.reasons[reason.key] || 0 }}</span>
              </div>
            </div>
          </div>

          <button @click="resetVotes" class="btn btn-danger" style="margin-top: 1rem">
            🗑️ Réinitialiser les votes
          </button>
          <button @click="showVoteResults = false" class="btn btn-primary" style="margin-top: 0.5rem">Fermer</button>
        </div>
      </div>

      <!-- Fullscreen modal -->
      <div v-if="showWasteToKids && featureWaste" class="fullscreen-modal">
        <button @click="showWasteToKids = false" class="fullscreen-close">✕ Fermer</button>
        <waste-input-panel :canteen-id="canteenId" variant="barquette" />
      </div>
    </div>
    <!-- /supervisor -->

    <!-- ═══════════════════════════════════════════════
         VUE 2 — BORNE VOTE ENFANT (Consumer / Kiosk)
         EPIC MVP 5 & 6
    ════════════════════════════════════════════════ -->
    <div v-if="currentView === 'kiosk'" class="kiosk-root">
      <!-- Bouton retour superviseur (pour la démo) -->
      <button class="kiosk-back" @click="currentView = 'supervisor'">← Retour superviseur</button>

      <!-- ── ÉTAPE 0 : Écran d'accueil ────────────────── -->
      <transition name="slide-fade" mode="out-in">
        <div v-if="kioskStep === 0" key="step0" class="kiosk-screen kiosk-welcome">
          <div class="kiosk-mascot">🧒</div>
          <h1 class="kiosk-welcome-title">À toi de voter !</h1>
          <p class="kiosk-welcome-sub">Dis-nous ce que tu as pensé du repas</p>
          <button class="kiosk-start-btn" @click="startVote">Commencer 👆</button>
        </div>

        <!-- ── ÉTAPE 1 : As-tu aimé le repas ? ─────────── -->
        <div v-else-if="kioskStep === 1" key="step1" class="kiosk-screen">
          <div class="kiosk-progress">
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot"></div>
          </div>
          <h2 class="kiosk-question">
            As-tu aimé
            <br />
            le repas ?
          </h2>
          <div class="kiosk-choices kiosk-choices-3">
            <button class="kiosk-choice-btn choice-green" @click="selectMealRating('liked')">
              <span class="kiosk-smiley">😄</span>
              <span class="kiosk-choice-label">Oui !</span>
            </button>
            <button class="kiosk-choice-btn choice-orange" @click="selectMealRating('medium')">
              <span class="kiosk-smiley">😐</span>
              <span class="kiosk-choice-label">Moyen</span>
            </button>
            <button class="kiosk-choice-btn choice-red" @click="selectMealRating('disliked')">
              <span class="kiosk-smiley">😞</span>
              <span class="kiosk-choice-label">Non</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 2 : As-tu assez mangé ? ────────────── -->
        <div v-else-if="kioskStep === 2" key="step2" class="kiosk-screen">
          <div class="kiosk-progress">
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot active"></div>
          </div>
          <h2 class="kiosk-question">
            As-tu assez
            <br />
            mangé ?
          </h2>
          <div class="kiosk-choices kiosk-choices-2">
            <button class="kiosk-choice-btn choice-green" @click="selectEnough('yes')">
              <span class="kiosk-smiley">✅</span>
              <span class="kiosk-choice-label">Oui !</span>
            </button>
            <button class="kiosk-choice-btn choice-red" @click="selectEnough('no')">
              <span class="kiosk-smiley">❌</span>
              <span class="kiosk-choice-label">Non</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 3 (conditionnel MVP 6) : Pourquoi ? ── -->
        <div v-else-if="kioskStep === 3" key="step3" class="kiosk-screen">
          <div class="kiosk-progress">
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot active"></div>
            <div class="kiosk-progress-dot active"></div>
          </div>
          <h2 class="kiosk-question">
            Pourquoi tu
            <br />
            n'as pas assez mangé ?
          </h2>
          <div class="kiosk-choices kiosk-choices-col">
            <button
              v-for="reason in notEnoughReasons"
              :key="reason.key"
              class="kiosk-reason-btn"
              @click="selectReason(reason.key)"
            >
              <span class="kiosk-reason-icon">{{ reason.icon }}</span>
              <span class="kiosk-reason-label">{{ reason.label }}</span>
            </button>
          </div>
        </div>

        <!-- ── ÉTAPE 4 : Confirmation ──────────────────── -->
        <div v-else-if="kioskStep === 4" key="step4" class="kiosk-screen kiosk-confirm">
          <div class="kiosk-confirm-icon">🎉</div>
          <h2 class="kiosk-confirm-title">Merci !</h2>
          <p class="kiosk-confirm-sub">Ton avis a bien été enregistré</p>
          <div class="kiosk-confirm-recap">
            <span v-if="currentVote.mealRating === 'liked'">😄 Tu as aimé le repas</span>
            <span v-if="currentVote.mealRating === 'medium'">😐 Le repas était moyen</span>
            <span v-if="currentVote.mealRating === 'disliked'">😞 Tu n'as pas aimé le repas</span>
            <br />
            <span v-if="currentVote.enough === 'yes'">✅ Tu as assez mangé</span>
            <span v-if="currentVote.enough === 'no'">❌ Tu n'as pas assez mangé</span>
          </div>
          <!-- Auto-reset après 3s, ou clic -->
          <button class="kiosk-next-btn" @click="resetKiosk">Suivant →</button>
        </div>
      </transition>
    </div>
    <!-- /kiosk -->
  </div>
</template>

<script>
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import CommunicationPanel2 from "@/components/CommunicationPanel2.vue"

export default {
  name: "SupervisorDashboard4",
  components: {
    WasteInputPanel,
    DashboardContextHeader,
    CommunicationPanel2,
  },

  data() {
    return {
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      effectifVariant: "SIMPLE",
      // ── Navigation ──────────────────────────────────
      currentView: "supervisor",
      views: [
        { key: "supervisor", label: "👩‍💼 Superviseur" },
        { key: "kiosk", label: "📲 Borne enfant" },
      ],

      // ── Supervisor modals ────────────────────────────
      showGlobalFeedback: false,
      showDetailedFeedback: false,
      showContextForm: false,
      showCommunications: false,
      showWasteToKids: false,
      showVoteResults: false,
      featureCommunications: true,
      featureWaste: true,
      featureVotes: true,

      // ── Supervisor data ──────────────────────────────
      emojis: ["😞", "😕", "😐", "😊", "😍"],
      globalRating: null,
      noiseLevel: 3,
      noiseOptions: [
        { key: 1, icon: "😊", label: "Calme" },
        { key: 2, icon: "🙂", label: "Plutôt calme" },
        { key: 3, icon: "😐", label: "Normal" },
        { key: 4, icon: "😟", label: "Bruyant" },
        { key: 5, icon: "😱", label: "Très bruyant" },
      ],
      temperature: 22,
      weather: "sunny",
      weatherOptions: [
        { key: "sunny", icon: "☀️", label: "Ensoleillé" },
        { key: "cloudy", icon: "☁️", label: "Nuageux" },
        { key: "rainy", icon: "🌧️", label: "Pluvieux" },
      ],
      dishes: [
        { name: "Poulet rôti", rating: null, comment: '"Trop sec"' },
        { name: "Riz", rating: null, comment: '"Portion trop grande"' },
        { name: "Haricots verts", rating: null, comment: "" },
        { name: "Yaourt", rating: null, comment: "" },
      ],

      // ── Kiosk (MVP 5 & 6) ────────────────────────────
      kioskStep: 0, // 0=accueil, 1=Q1, 2=Q2, 3=Q3(conditionnel), 4=confirm
      currentVote: {
        mealRating: null, // 'liked' | 'medium' | 'disliked'
        enough: null, // 'yes' | 'no'
        reason: null, // 'quantity' | 'taste' | 'other'
      },
      childVotes: [], // historique du service

      notEnoughReasons: [
        { key: "quantity", icon: "🥄", label: "Pas assez de quantité" },
        { key: "taste", icon: "🤢", label: "Je n'ai pas aimé" },
        { key: "other", icon: "🤔", label: "Autre raison" },
      ],
    }
  },

  computed: {
    childVoteSummary() {
      const base = {
        liked: 0,
        medium: 0,
        disliked: 0,
        enoughYes: 0,
        enoughNo: 0,
        reasons: { quantity: 0, taste: 0, other: 0 },
      }
      this.childVotes.forEach((v) => {
        if (v.mealRating) base[v.mealRating]++
        if (v.enough === "yes") base.enoughYes++
        if (v.enough === "no") base.enoughNo++
        if (v.reason) base.reasons[v.reason]++
      })
      return base
    },
  },
  methods: {
    // ── Supervisor methods ───────────────────────────
    onContextChange({
      canteenId,
      selectedDate,
      selectedService,
      effectifVariant,
      featureCommunications,
      featureWaste,
      featureVotes,
    }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.effectifVariant = effectifVariant || "SIMPLE"
      this.featureCommunications = featureCommunications ?? true
      this.featureWaste = featureWaste ?? true
      this.featureVotes = featureVotes ?? true
    },
    getRatingLabel(rating) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return rating ? labels[rating - 1] : "Non évalué"
    },
    getRatingLabelByIndex(idx) {
      const labels = ["Très mauvais", "Mauvais", "Correct", "Très bien", "Excellent"]
      return labels[idx]
    },
    updateDishRating(idx, rating) {
      this.dishes[idx].rating = rating
    },
    editDishComment(idx) {
      const c = prompt("Commentaire pour " + this.dishes[idx].name + ":", this.dishes[idx].comment || "")
      if (c !== null) this.dishes[idx].comment = c
    },
    saveGlobalRating() {
      this.showGlobalFeedback = false
    },
    saveContext() {
      this.showContextForm = false
    },
    addComment() {
      prompt("Votre commentaire :")
    },
    takePhoto() {
      alert("Ouverture de l'appareil photo")
    },
    resetVotes() {
      if (confirm("Réinitialiser tous les votes enfants ?")) {
        this.childVotes = []
        this.showVoteResults = false
      }
    },

    // ── Percentage helpers ───────────────────────────
    pct(n) {
      if (!this.childVotes.length) return 0
      return Math.round((n / this.childVotes.length) * 100)
    },
    pctOf(n, total) {
      if (!total) return 0
      return Math.round((n / total) * 100)
    },

    // ── Kiosk methods (MVP 5 & 6) ────────────────────
    startVote() {
      this.currentVote = { mealRating: null, enough: null, reason: null }
      this.kioskStep = 1
    },
    selectMealRating(val) {
      this.currentVote.mealRating = val
      this.kioskStep = 2
    },
    selectEnough(val) {
      this.currentVote.enough = val
      // MVP 6 : déclenchement conditionnel
      if (val === "no") {
        this.kioskStep = 3
      } else {
        this.submitVote()
      }
    },
    selectReason(key) {
      this.currentVote.reason = key
      this.submitVote()
    },
    submitVote() {
      // Enregistrement anonyme
      this.childVotes.push({ ...this.currentVote, ts: Date.now() })
      this.kioskStep = 4
      // Auto-reset après 3 secondes
      setTimeout(() => {
        if (this.kioskStep === 4) this.resetKiosk()
      }, 3000)
    },
    resetKiosk() {
      this.kioskStep = 0
      this.currentVote = { mealRating: null, enough: null, reason: null }
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════════════════════════
   ROOT & VIEW SWITCHER
══════════════════════════════════════════════════════ */
.app-root {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  min-height: 100vh;
  background: #f8f9fa;
}

.view-switcher {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 0;
  background: #fff;
  border-bottom: 2px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
}

.view-tab {
  padding: 0.6rem 1.25rem;
  border: 2px solid transparent;
  border-radius: 8px 8px 0 0;
  background: #f3f4f6;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
}
.view-tab.active {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

/* ══════════════════════════════════════════════════════
   SUPERVISOR DASHBOARD
══════════════════════════════════════════════════════ */
.supervisor-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}
.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.25rem 0;
}
.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1rem 0;
}

/* Vote summary bar */
.vote-summary-bar {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  background: #f0fdf4;
  border: 1.5px solid #86efac;
  border-radius: 999px;
  padding: 0.4rem 1.2rem;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: 0.5rem;
}
.vote-summary-label {
  color: #166534;
}
.vote-pill {
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
}
.pill-green {
  background: #d1fae5;
  color: #065f46;
}
.pill-orange {
  background: #fed7aa;
  color: #9a3412;
}
.pill-red {
  background: #fee2e2;
  color: #991b1b;
}
.pill-blue {
  background: #dbeafe;
  color: #1e40af;
}

/* Cards grid */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}
.card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid #e5e7eb;
}
.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}
.card-featured {
  border: 2px solid #f97316;
  background: linear-gradient(135deg, #fff7ed 0%, white 100%);
}
.card-highlight {
  border: 2px solid #8b5cf6;
  background: linear-gradient(135deg, #faf5ff 0%, white 100%);
}
.card-votes {
  border: 2px solid #10b981;
  background: linear-gradient(135deg, #f0fdf4 0%, white 100%);
}
.card-kiosk {
  border: 2px solid #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, white 100%);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}
.card-description {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0 0 1rem 0;
}
.card-link {
  display: inline-flex;
  font-size: 0.875rem;
  color: #2563eb;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.action-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}
.action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.action-btn:hover {
  background: #eff6ff;
  border-color: #2563eb;
}
.action-icon {
  font-size: 2rem;
}

/* Mini bars in vote card */
.vote-mini-bars {
  margin: 0.75rem 0;
}
.mini-bar-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
  font-size: 1.1rem;
}
.mini-bar-track {
  flex: 1;
  background: #f3f4f6;
  border-radius: 999px;
  height: 8px;
  overflow: hidden;
}
.mini-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.5s ease;
}
.fill-green {
  background: #10b981;
}
.fill-orange {
  background: #f59e0b;
}
.fill-red {
  background: #ef4444;
}
.fill-blue {
  background: #3b82f6;
}
.mini-bar-val {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 2.5rem;
  text-align: right;
}

/* Badges */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
}
.badge-blue {
  background: #dbeafe;
  color: #1e40af;
}
.badge-orange {
  background: #fed7aa;
  color: #9a3412;
}
.badge-green {
  background: #d1fae5;
  color: #065f46;
}
.badge-purple {
  background: #ede9fe;
  color: #5b21b6;
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}
.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}
.modal-large {
  max-width: 700px;
}
.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}
.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}
.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Result bars (vote results modal) */
.result-section {
  margin-bottom: 1.75rem;
}
.result-bars {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.result-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.result-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  min-width: 9rem;
}
.result-track {
  flex: 1;
  background: #f3f4f6;
  border-radius: 999px;
  height: 14px;
  overflow: hidden;
}
.result-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.6s ease;
}
.result-count {
  font-size: 0.82rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 4.5rem;
  text-align: right;
}

/* Rating selector */
.rating-selector {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.rating-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 0.5rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-btn:hover {
  background: #eff6ff;
}
.rating-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
  transform: scale(1.05);
}
.rating-emoji {
  font-size: 2.5rem;
}
.rating-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #6b7280;
  text-align: center;
}

/* Dishes */
.dishes-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
}
.dish-card {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 12px;
}
.dish-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.dish-ratings {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.rating-mini-btn {
  font-size: 1.5rem;
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}
.rating-mini-btn.active {
  border-color: #2563eb;
  background: #eff6ff;
  transform: scale(1.1);
}
.rating-percent {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}
.dish-comment {
  font-size: 0.875rem;
  color: #f97316;
  font-style: italic;
  margin: 0.75rem 0 0 0;
}

/* Context */
.detail-section {
  margin-bottom: 1.5rem;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}
.weather-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}
.weather-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}
.weather-btn.active {
  background: #eff6ff;
  border-color: #2563eb;
}
.weather-icon {
  font-size: 2.5rem;
}
.weather-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
}
.temperature-input {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.input-field {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
}
.input-field:focus {
  outline: none;
  border-color: #2563eb;
}
.temperature-unit {
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
}
.noise-selector {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
.noise-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;
}
.noise-btn.active {
  border-color: #3b82f6;
  background: #eff6ff;
}
.noise-icon {
  font-size: 1.5rem;
}
.noise-label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Messages */
.messages-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.message-card {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}
.message-absence {
  background: #fef2f2;
  border-color: #ef4444;
}
.message-info {
  background: #eff6ff;
  border-color: #2563eb;
}
.message-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}
.message-source {
  font-size: 0.75rem;
  color: #6b7280;
}
.message-content {
  font-size: 0.875rem;
  color: #4b5563;
  margin: 0;
}

/* Fullscreen */
.fullscreen-modal {
  position: fixed;
  inset: 0;
  background: white;
  z-index: 2000;
  overflow-y: auto;
  padding: 1rem;
}
.fullscreen-close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  z-index: 2001;
}

/* Buttons */
.btn {
  display: block;
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  margin-top: 0.75rem;
  font-size: 1rem;
}
.btn-primary {
  background: #2563eb;
  color: white;
}
.btn-danger {
  background: #ef4444;
  color: white;
}

/* Menu */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}
.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}
.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}
.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* ══════════════════════════════════════════════════════
   KIOSK — BORNE VOTE ENFANT (MVP 5 & 6)
══════════════════════════════════════════════════════ */
.kiosk-root {
  position: relative;
  min-height: calc(100vh - 58px);
  background: linear-gradient(160deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.kiosk-back {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.25);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.85rem;
  backdrop-filter: blur(4px);
  z-index: 10;
}

.kiosk-screen {
  width: 100%;
  max-width: 480px;
  text-align: center;
}

/* Welcome */
.kiosk-welcome {
}
.kiosk-mascot {
  font-size: 6rem;
  margin-bottom: 1rem;
  animation: bounce 1.2s infinite;
}
@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-12px);
  }
}
.kiosk-welcome-title {
  font-size: 3rem;
  font-weight: 800;
  color: white;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.kiosk-welcome-sub {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 2.5rem 0;
}
.kiosk-start-btn {
  background: white;
  color: #764ba2;
  border: none;
  padding: 1.25rem 3rem;
  border-radius: 999px;
  font-size: 1.5rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: transform 0.15s;
}
.kiosk-start-btn:active {
  transform: scale(0.97);
}

/* Progress dots */
.kiosk-progress {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
}
.kiosk-progress-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.35);
  transition: background 0.3s;
}
.kiosk-progress-dot.active {
  background: white;
}

/* Question */
.kiosk-question {
  font-size: 2.2rem;
  font-weight: 800;
  color: white;
  line-height: 1.2;
  margin: 0 0 2.5rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Choice buttons */
.kiosk-choices {
  display: flex;
  justify-content: center;
  gap: 1.25rem;
}
.kiosk-choices-3 {
  flex-direction: row;
  flex-wrap: wrap;
}
.kiosk-choices-2 {
  flex-direction: row;
}

.kiosk-choice-btn {
  flex: 1;
  min-width: 110px;
  max-width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}
.kiosk-choice-btn:active {
  transform: scale(0.94);
}

.choice-green {
  background: #d1fae5;
}
.choice-orange {
  background: #fed7aa;
}
.choice-red {
  background: #fee2e2;
}

.kiosk-smiley {
  font-size: 4rem;
}
.kiosk-choice-label {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1a1a1a;
}

/* Reason buttons (Q3 conditionnelle MVP 6) */
.kiosk-choices-col {
  flex-direction: column;
  gap: 1rem;
  align-items: stretch;
  max-width: 400px;
  margin: 0 auto;
}
.kiosk-reason-btn {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 1.75rem;
  background: white;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transition: transform 0.12s, box-shadow 0.12s;
}
.kiosk-reason-btn:active {
  transform: scale(0.97);
}
.kiosk-reason-icon {
  font-size: 2.2rem;
}
.kiosk-reason-label {
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: left;
}

/* Confirmation */
.kiosk-confirm {
}
.kiosk-confirm-icon {
  font-size: 7rem;
  animation: pop 0.4s cubic-bezier(0.17, 0.67, 0.42, 1.35);
}
@keyframes pop {
  0% {
    transform: scale(0.3);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
.kiosk-confirm-title {
  font-size: 3.5rem;
  font-weight: 900;
  color: white;
  margin: 0.5rem 0;
}
.kiosk-confirm-sub {
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  margin: 0 0 1.5rem 0;
}
.kiosk-confirm-recap {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  border-radius: 16px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  line-height: 2;
  margin-bottom: 2rem;
}
.kiosk-next-btn {
  background: white;
  color: #764ba2;
  border: none;
  padding: 1rem 2.5rem;
  border-radius: 999px;
  font-size: 1.25rem;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}
.slide-fade-enter {
  transform: translateX(40px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}

/* ── Responsive ─────────────────────────────────── */
@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
  .rating-selector {
    grid-template-columns: repeat(3, 1fr);
  }
  .kiosk-question {
    font-size: 1.75rem;
  }
  .kiosk-choice-btn {
    padding: 1.25rem 0.75rem;
  }
  .kiosk-smiley {
    font-size: 3rem;
  }
}
</style>
